import { Injectable, inject , signal } from '@angular/core';
import { Observable, Observer, BehaviorSubject, forkJoin, of, throwError } from 'rxjs';
import { WaypointService } from '../core/waypoint.service';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { WebviewService } from '../platform/webview.service';
import { SettingsService } from './settings.service';
import device from 'current-device';
import { ConsoleLoggerService } from '../logging/console-logger.service';
import { retry, switchMap } from 'rxjs/operators';
import { timer } from 'rxjs';
import { LanguageService } from './language.service';
import { HistoryElement } from '../../models/history';
import { ECar, ECarSettings } from '../../models/ecar';
import { ECarType } from '../../models/ecar-type';
import { User } from '../../models/user';
import { UserProfile } from '../../models/user-profile';
import { TripPoint } from '../../models/trip';
import { SubscriptionPlatform, UserSubscription } from '../../models/user-subscription';
import { HttpService } from '../core/http.service';

export const enum SubscriptionType {
  Free = "free",
  ProMonthly = "evnavigation.pro.monthly",
  ProYearly = "evnavigation.pro.yearly",
  ProplusMonthly = "evnavigation.proplus.monthly",
  ProplusYearly = "evnavigation.proplus.yearly",
  ProplusVoucher = "evnavigation.proplus.voucher"
}

@Injectable({
  providedIn: 'root'
})

export class AccountService {

  private readonly _isAuthorized = signal<boolean>(false);
  public ObservableIsAuthorized = new BehaviorSubject<boolean | null>(null);
  public ObservableUser = new BehaviorSubject<User | null>(null);
  public ObservableUserVehicles = new BehaviorSubject<ECar[] | null>(null);
  private selectedUserCar: ECar | null = null;
  public ObservableSelectedUserCar = new BehaviorSubject<ECar | null>(null);
  public ObservableOpenCarSelectorDialog = new BehaviorSubject<boolean | null>(null);
  public ObservableVehicleLoader = new BehaviorSubject<boolean | null>(null);
  public ObservableOpenEditProfileDialog = new BehaviorSubject<boolean | null>(null);
  public ObservableSocialLoginResponse = new BehaviorSubject<{ AuthResponse: boolean, ErrorMessage: boolean } | null>(null);
  public ObservableSubscriptionType!: BehaviorSubject<SubscriptionType>;
  public ObservableSubscriptionFailed = new BehaviorSubject<boolean>(false);
  public ObservableSubscriptionLoader = new BehaviorSubject<boolean>(false);
  private historyLastSelectedVehicle: ECar | null = null;
  private userSubscription: UserSubscription = {
    AutoRenew: false, ExpireDate: null!, LastPaymentDate: null!, NextPaymentDate: null!, SubscriptionType: SubscriptionType.Free,
    Platform: SubscriptionPlatform.Web, DowngradeAtPeriodEnd: false
  };
  public ObservableSubscriptionChangedDialog = new BehaviorSubject<SubscriptionType | null>(null);
  public ObservableVoucherInfoDialog = new BehaviorSubject<any>(null);

  public accountInit: boolean = false;
  public user!: User;
  private ECars!: ECarType[];
  private readonly _social = signal<boolean>(false);
  /** Signal-backed: read from templates to decide whether to offer a password change. */
  public get social(): boolean { return this._social(); }
  public set social(value: boolean) { this._social.set(value); }
  private provider: string = "";

  private readonly waypointService = inject(WaypointService);
  private readonly cookieService = inject(CookieService);
  public readonly webviewService = inject(WebviewService);
  private readonly settingsService = inject(SettingsService);
  private readonly consoleLoggerService = inject(ConsoleLoggerService);
  private readonly languageService = inject(LanguageService);
  private readonly httpService = inject(HttpService);

  constructor() {
    this.user = new User();
    this.user.Profile = new UserProfile();
    this.ObservableSubscriptionType = new BehaviorSubject<SubscriptionType>(this.userSubscription.SubscriptionType);

    this.webviewService.AddAppleSignInCallback((isSuccess: boolean, userId: string, accessToken: string) => {
      this.handleAppleSignInResponse(isSuccess, userId, accessToken);
    });
    this.webviewService.addSubscriptionSuccessCallback((isSuccess: boolean, subscriptionType: SubscriptionType) => {
      if (isSuccess) {
        this.handleSubscriptionSuccessCallback(subscriptionType);
        this.ObservableSubscriptionLoader.next(true);
      }
    });
    this.webviewService.addSubscriptionCancelCallback(() => {
      this.handleSubscriptionCancelCallback();
    });

  }

  /** Retry cadence while waiting for the async GIS script / its rendered button. */
  private static readonly GIS_POLL_MS = 100;
  private static readonly GIS_POLL_MAX = 50;

  /** Off-screen host of the official GIS button; see initGoogleLogin(). */
  private gisButtonHost: HTMLElement | null = null;

  public initGoogleLogin(attempt: number = 0): void {
    const gis = (window as any)['google']?.accounts?.id;
    if (!gis) {
      // The gsi/client script is loaded async and may land after bootstrap.
      if (attempt < AccountService.GIS_POLL_MAX) {
        setTimeout(() => this.initGoogleLogin(attempt + 1), AccountService.GIS_POLL_MS);
      }
      return;
    }

    gis.initialize({
      client_id: '27060447138-oun6fqos4lgeas6okc07f0d10f9vns69.apps.googleusercontent.com',
      cookiepolicy: 'single_host_origin',
      scope: 'profile email',
      // Keep the button on the classic popup flow. FedCM (One Tap, or the button's
      // FedCM mode) is embargoed by Chrome for the site once the user dismisses it.
      use_fedcm_for_button: false,
      callback: (resp) => {
        this.handleGoogleSignInResponse(true, resp.clientId, resp.credential);
      }
    });

    // Sign In With Google on the web goes through Google's rendered button: its click
    // opens the account-chooser popup, which sits above any modal and does not depend
    // on FedCM. The button markup arrives asynchronously from Google, so it is rendered
    // once here, invisibly, and the custom "G" buttons in the account dialog forward
    // their clicks to it (signInWithGoogle). accounts.id.prompt() (One Tap) is not used:
    // Firefox draws it as an in-page iframe under the modal backdrop, and on Chromium
    // >= 142 the GIS client forces it through FedCM, which Chrome disables for the site
    // after the user dismisses it once.
    const host = document.createElement('div');
    host.setAttribute('data-testid', 'google-sign-in-hidden-btn');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;overflow:hidden;visibility:hidden;';
    document.body.appendChild(host);
    gis.renderButton(host, { type: 'icon', shape: 'circle', size: 'large', theme: 'outline' });
    this.gisButtonHost = host;
  }

  // AUTHORIZATION

  /** Reading this from a template registers the view as a consumer of the auth signal. */
  public getIsAuthorized(): boolean {
    return this._isAuthorized();
  }

  public setIsAuthorized(isAuthorized: boolean): void {
    this._isAuthorized.set(isAuthorized);
    this.settingsService.setIsAuthorized(isAuthorized);
    this.ObservableIsAuthorized.next(isAuthorized);
  }

  public setToken(token: string, expires: number, socialProvider?: string) {
    this.httpService.setToken(token);
    this.setIsAuthorized(true);

    if (expires) {
      this.cookieService.set("evt", socialProvider ? token + "social" : token, expires, "/");
      this.webviewService.SetAccessToken(token, expires);
    }

    if (socialProvider) {
      this.social = true;
      this.provider = socialProvider;
      this.cookieService.set("accountProvider", socialProvider);
    }
  }

  // REGISTRATION

  // checking email address availability
  public isExist(emailAddress: string): Observable<any> {
    return this.httpService.checkUserExist(emailAddress);
  }

  // checking the country of your location
  public getCountry(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.getUserCountry().subscribe({
        next: (resp: any) => {
          observer.next(resp);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
          observer.complete();
        }
      });
    });
  }

  // registering a new user
  public registerUser(profile: UserProfile, password: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.registerUser(profile, password, this.languageService.getLanguage())
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            if (error.error && error.error.error && error.error.error.avatar) {
              observer.error({ failed: { error: error.error.error.avatar } });
            }
            else {
              observer.error({ failed: { error: this.languageService.languageJSON.Global_SomethingWentWrong } });
            }
            observer.complete();
          }
        });
    });
  }

  // email verification for activation profile
  public verifyEmail(urlParam: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.verifyEmail(urlParam)
        .subscribe({
          next: (resp: any) => {
            this.setToken(resp.success.token, 1);
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // send a new email with activation url
  public resendActivationEmail(emailAddress: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.resendEmail(emailAddress)
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error({ failed: { error: this.languageService.languageJSON.Global_SomethingWentWrong } });
            observer.complete();
          }
        });
    });
  }

  // SOCIAL REGISTRATION/LOGIN

  // login with social media account
  public authenticateSocial(provider: string, providerId: string, accessToken: string, native?: boolean): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.authenticateSocial(provider, providerId, accessToken, this.webviewService.IsGPSTWebview(), native)
        .subscribe({
          next: (resp: any) => {
            if (resp.success) {
              this.initSocialProfile(resp, provider).subscribe({
                next: (respInit) => {
                  observer.next(resp);
                  observer.complete();
                },
                error: (error) => {
                  observer.error(error);
                  observer.complete();
                }
              });
            }
          },
          error: (error) => {
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // set authorization, store token in cookies
  public initSocialProfile(resp: any, provider: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.setToken(resp.success.token, 14, provider);

      this.loadUserProfile().subscribe({
        next: () => {
          if (resp.success.error || (!this.user.Profile.FirstName || !this.user.Profile.LastName)) {
            this.ObservableOpenEditProfileDialog.next(true);
          }
          // wait user profile/settings/subscriptions/vehicles http calls
          this.initUserData(false, true, true).subscribe({
            next: () => {
              observer.next(resp);
              observer.complete();
            },
            error: (error) => {
              observer.error(error);
              observer.complete();
            }
          });
        },
        error: (error) => {
          observer.error(error);
          observer.complete();
        }
      });
    });
  }

  // LOGIN, INITIALIZATION

  // user login
  public login(login: { email: string, password: string, rememberMe: boolean }): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.loginUser(login.email, login.password)
        .subscribe({
          next: (resp: any) => {
            if (resp.success) {
              this.setToken(resp.success.token, login.rememberMe ? 365 : 1);
              this.initUserData(true, true, true).subscribe({
                next: () => {
                  observer.next(resp);
                  observer.complete();
                },
                error: (error) => {
                  observer.error(error);
                  observer.complete();
                }
              });
            }

            if (resp.failed && resp.failed == "USER_ERR_205") {
              observer.next(resp);
              observer.complete();
            }
          },
          error: (error) => {
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public initAccount(): boolean {
    let evtoken = this.cookieService.get("evt");
    if (evtoken && evtoken != "null") {
      if (evtoken.slice(-6) == "social") {
        this.setToken(evtoken.slice(0, -6), 0);
        this.social = true;
      }
      else {
        this.setToken(evtoken, 0);
      }
      this.setIsAuthorized(true);
    }

    const accountProvider = this.cookieService.get("accountProvider");
    if (accountProvider && accountProvider != "null") {
      this.provider = accountProvider;
    }

    return this._isAuthorized();
  }

  public initUserData(loadUserProfile: boolean, getUserSettings: boolean, getUserVehicles: boolean): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      const accountHttpCalls: Observable<any>[] = [];

      if (loadUserProfile) {
        accountHttpCalls.push(this.loadUserProfile());
      }
      if (getUserSettings) {
        accountHttpCalls.push(this.getUserSettings());
      }
      if (getUserVehicles) {
        accountHttpCalls.push(this.getUserVehicles());
      }

      // wait user profile/settings/subscriptions/vehicles http calls
      forkJoin(accountHttpCalls).subscribe({
        next: (resp) => {
          observer.next(resp);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
          observer.complete();
        }
      });
    });
  }

  // get user profile data
  public loadUserProfile(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.getUser()
        .subscribe({
          next: (resp: any) => {
            if (resp.email) {
              this.setUserData(resp);
              this.parseUserSubscription(resp);
            }
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public setUserData(resp: any): void {
    this.user.Profile.Email = resp.email;
    this.user.Profile.FirstName = resp.first_name;
    this.user.Profile.LastName = resp.last_name;
    this.user.Profile.Country = resp.country;
    this.user.Profile.Phone = resp.phone_number;
    this.user.Profile.Newsletter = resp.newsletter;
    this.user.Profile.Avatar = resp.avatar;
    this.user.Profile.Id = resp.id;
    if (this.webviewService.IsGPSTWebview()) {
      this.webviewService.SetUserId(resp.id);
    }
    if (this.user.Profile.Avatar == "users/default.png" || this.user.Profile.Avatar == "users/avatar/default.png") {
      this.user.Profile.Avatar = undefined;
    }
    if (resp.addresses != null) {
      this.settingsService.setAddresses(this.settingsService.addressesJSONtoAddresses(JSON.parse(resp.addresses)));
    }

    if (resp.stations) {
      const stations = JSON.parse(resp.stations);
      if (stations && stations.disabled) {
        this.settingsService.setDisabledChargingStationIds(stations.disabled);
      }
      else {
        this.settingsService.setDisabledChargingStationIds([]);
      }
      if (stations && stations.favorite) {
        this.settingsService.setFavoriteChargingStationIds(stations.favorite);
      }
      else {
        this.settingsService.setFavoriteChargingStationIds([]);
      }
    }
    else {
      this.settingsService.setDisabledChargingStationIds([]);
      this.settingsService.setFavoriteChargingStationIds([]);
    }

    if (resp.operators) {
      const operators = JSON.parse(resp.operators);
      if (operators && operators.disabled) {
        this.settingsService.setDisabledChargingOperatorIds(operators.disabled.map(Number));
      }
      else {
        this.settingsService.setDisabledChargingOperatorIds([]);
      }
      if (operators && operators.favorite) {
        this.settingsService.setFavoriteChargingOperatorIds(operators.favorite.map(Number));
      }
      else {
        this.settingsService.setFavoriteChargingOperatorIds([]);
      }
    }
    else {
      this.settingsService.setDisabledChargingOperatorIds([]);
      this.settingsService.setFavoriteChargingOperatorIds([]);
    }

    if (resp.disabledChargingStation != null) {
      this.settingsService.setDisabledChargingStationIds(JSON.parse(resp.disabledChargingStation));
    }

    if (resp.stripe_id){
      this.user.TrialUsed = true;
    }

    this.ObservableUser.next(this.user);
  }

  public parseUserSubscription(resp: any): void {
    if (resp.subscriptions?.subscription_type || resp.subscriptions?.type == "voucher") {
      let subscriptionType = "evnavigation." + resp.subscriptions?.subscription_type + "." + resp.subscriptions?.renew_type;

      const userSubscription: UserSubscription = {
        AutoRenew: resp.subscriptions.autorenew == "true",
        ExpireDate: new Date(resp.subscriptions.expire_date),
        LastPaymentDate: new Date(resp.subscriptions.last_payment_date),
        NextPaymentDate: resp.subscriptions.next_payment_date ? new Date(resp.subscriptions.next_payment_date) : null,
        SubscriptionType: subscriptionType as SubscriptionType,
        Platform: resp.subscriptions.type,
        DowngradeAtPeriodEnd: resp.subscriptions.downgrade_at_period_end == 1
      }

      if (resp.subscriptions?.type == "voucher") {
        userSubscription.SubscriptionType = SubscriptionType.ProplusVoucher;
        userSubscription.AutoRenew = false;
      }

      this.setUserSubscription(userSubscription);
    }
    else {
      this.setUserSubscription({
        AutoRenew: false, ExpireDate: null!, LastPaymentDate: null!, NextPaymentDate: null!, SubscriptionType: SubscriptionType.Free,
        Platform: SubscriptionPlatform.Web, DowngradeAtPeriodEnd: false
      });
    }

    if (!this.getUserSubscriptionType().includes(".proplus") && localStorage.getItem("voucherToken")) {
      this.addVoucherSubscription(localStorage.getItem("voucherToken")!).subscribe();
    }
  }

  public initAllVehicles(allVehicles: ECarType[]): void {
    this.ECars = allVehicles;
    // init user
    if (this.user.Ecars != null && this.user.Ecars != undefined && this.user.Ecars.length > 0) {
      for (let userCarIdx = 0; userCarIdx < this.user.Ecars.length; ++userCarIdx) {
        const userEcar = this.user.Ecars[userCarIdx];
        for (let i = 0; i < this.ECars.length; ++i) {
          if (this.ECars[i].Id == userEcar.userVehicleID) {
            userEcar.ECarType = { ...this.ECars[i] };
            break;
          }
        }
      }
    }
    if (this.accountInit == false) {
      this.initAccount();
      this.accountInit = true;
    }
  }

  // getting user vehicle list
  public getUserVehicles(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.ObservableVehicleLoader.next(true);
      this.httpService.getUserVehicles().subscribe({
        next: (resp: any) => {
          this.ObservableVehicleLoader.next(false);
          if (resp.success && resp.success.length > 0) {
            this.initUserVehicles(resp.success);
          }
          else {
            // Empty vehicle list -> signal the app to (re)show the mandatory first-vehicle
            // dialog. RouterFlowHandler subscribes to this and keeps it open until a
            // vehicle is saved. Fires on every empty result (startup, post email
            // verification, after deleting the last car).
            this.ObservableOpenCarSelectorDialog.next(true);
          }
          observer.next(resp);
          observer.complete();
        },
        error: (error) => {
          this.checkErrorStatus(error);
          observer.error(error);
          observer.complete();
        }
      });
    });
  }

  // processing user vehicles data
  private initUserVehicles(resp: any): void {
    if (resp) {
      this.user.Ecars = [];
      for (let carIdx = 0; carIdx < resp.length; carIdx++) {
        const carData = resp[carIdx];
        const userEcar = new ECar();
        userEcar.userVehicleID = carData.id;
        userEcar.name = (carData.name ? carData.name : "");
        userEcar.ECarType = null;
        userEcar.selected = ((carData.selected != undefined) && (carData.selected != undefined) && (parseInt(carData.selected) === 1));
        if (carData.settings != undefined) {
          userEcar.settings = JSON.parse(carData.settings);
        }
        if (this.ECars != null && this.ECars != undefined && this.ECars.length > 0) {
          for (let i = 0; i < this.ECars.length; ++i) {
            if (this.ECars[i].Id == carData.vehicle_id) {
              userEcar.ECarType = { ... this.ECars[i] };
              break;
            }
          }
        }
        if (userEcar.ECarType) {
          userEcar.ECarType.ChargerTypes = JSON.parse(carData.charger_types);
        }

        this.user.Ecars.push(userEcar);

        if (userEcar.selected === true && userEcar.ECarType != null && userEcar.ECarType.Icon) {
          this.selectedUserCar = userEcar;
          this.ObservableSelectedUserCar.next(userEcar);
        }
      }
      if (this.ECars != null && this.ECars != undefined && this.ECars.length > 0) {
        this.ObservableUserVehicles.next(this.user.Ecars);
      }
    }
  }

  // GETTING, SETTING USER DATA

  public getUserProfile(): User {
    return this.user;
  }

  // changing user profile
  public setUser(profile: UserProfile): Observable<any> {
    return new Observable((observer: Observer<any>) => {

      this.httpService.updateUserProfile(profile)
        .subscribe({
          next: (resp: any) => {
            // setting changed user profile values
            if (resp.success) {
              resp.success.newsletter = resp.success.newsletter == "1";
              this.setUserData(resp.success);
            }
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            if (error.status && error.status == 401 &&
              error.error && error.error.error && error.error.error.avatar) {
              //error msg
            }
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public getUserAvatar(): string {
    return this.user.Profile.Avatar as string;
  }

  public setUserSubscriptionType(subscriptionType: SubscriptionType) {
    this.userSubscription.SubscriptionType = subscriptionType;
    this.ObservableSubscriptionType.next(subscriptionType);
  }

  public getUserSubscriptionType(): SubscriptionType {
    return this.userSubscription.SubscriptionType;
  }

  public setUserSubscription(userSubscription: UserSubscription) {
    this.userSubscription = userSubscription;
    this.ObservableSubscriptionType.next(userSubscription.SubscriptionType);
  }

  public getUserSubscription(): UserSubscription {
    return this.userSubscription;
  }

  public cancelUserSubscription() {
    return new Observable((observer: Observer<any>) => {
      this.httpService.cancelUserSubscription()
        .subscribe({
          next: (resp: any) => {
            if (resp.message == "Subscription cancelled") {
              observer.next(resp);
              observer.complete();
            }
            else {
              observer.error("error");
              observer.complete();
            }
          },
          error: (error) => {
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // adding a new vehicle to user account
  public addVehicle(vehicleId: number, vehicleName: string, chargerTypes: number[], batteryStateOfHealth: number, fastChargeLimit: number, sliderSettings?: Partial<ECarSettings>): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      const settings = {
        "batteryLevel": sliderSettings?.batteryLevel ?? 73,
        "weight": sliderSettings?.weight ?? 2,
        "tirePressure": sliderSettings?.tirePressure ?? 2.3,
        "userBehavior": sliderSettings?.userBehavior ?? 50,
        "speedLimiter": sliderSettings?.speedLimiter ?? 100,
        "batterySafetyLimitRha": sliderSettings?.batterySafetyLimitRha ?? 50,
        "batterySafetyLimitRoute": sliderSettings?.batterySafetyLimitRoute ?? 15,
        "minBatteryAtDestination": sliderSettings?.minBatteryAtDestination ?? 10,
        "batteryStateOfHealth": batteryStateOfHealth, "fastChargeLimit": fastChargeLimit,
        "tripRecording": sliderSettings?.tripRecording ?? 1
      }
      this.httpService.addUserVehicle(vehicleId, vehicleName, chargerTypes, settings)
        .subscribe({
          next: (resp: any) => {
            if (resp.success && this.user.getSelectedCar() == undefined) {
              this.selectVehicle(resp.success.id).subscribe();
            }
            this.getUserVehicles().subscribe(() => {
              observer.next(resp);
              observer.complete();
            });
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // change active vehicle
  public selectVehicle(vehicleId: number): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.selectUserVehicle(vehicleId)
        .subscribe({
          next: (resp: any) => {
            this.getUserVehicles().subscribe(() => {
              observer.next(resp);
              observer.complete();
            });
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // change user vehicle details
  public editVehicle(userVehicleId: number, vehicleId: number | null, vehicleName: string | null, chargerTypes: number[] | null, settings: string, reloadVehicles: boolean): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.modifyUserVehicle(userVehicleId, vehicleId, vehicleName, chargerTypes, settings)
        .subscribe({
          next: (resp: any) => {
            if (reloadVehicles) {
              this.getUserVehicles().subscribe();
            }
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // remove a vehicle from the user account
  public removeVehicle(vehicleId): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.removeUserVehicle(vehicleId)
        .subscribe({
          next: (resp: any) => {
            this.getUserVehicles().subscribe();
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // modify user password
  public changePassword(emailAddress: string, currentPassword: string, newPassword: string, cNewPassword: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {

      this.httpService.changeUserPassword(emailAddress, currentPassword, newPassword, cNewPassword)
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // request resetting password
  public getForgottenPassword(emailAddress: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.requestPasswordReset(emailAddress)
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // load user settings
  public getUserSettings(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.getUserSettings()
        .subscribe({
          next: (resp: any) => {
            if (resp.success) {
              if (resp.success.mapsettings) { resp.success.mapsettings = JSON.parse(resp.success.mapsettings) };
              if (resp.success.routing) { resp.success.routing = JSON.parse(resp.success.routing) };
              if (resp.success.unit) { resp.success.unit = JSON.parse(resp.success.unit) };
              if (resp.success.voiceguidance) { resp.success.voiceguidance = JSON.parse(resp.success.voiceguidance) };
              if (resp.success.voiceInputTutorialDone) { resp.success.voiceInputTutorialDone = JSON.parse(resp.success.voiceInputTutorialDone) };
              if (resp.success.tracking) { resp.success.tracking = JSON.parse(resp.success.tracking); }

              if (resp.success.routing != undefined && resp.success.routing != null) {
                const routingSettings = this.settingsService.routingJSONtoRoutingSettings(resp.success.routing);

                if (!(this.settingsService.getRouting().Ferries == routingSettings.Ferries
                  && this.settingsService.getRouting().Highways == routingSettings.Highways
                  && this.settingsService.getRouting().TollRoads == routingSettings.TollRoads)) {
                  this.settingsService.setRounting(routingSettings);
                }
              }

              if (resp.success.voiceguidance != undefined && resp.success.voiceguidance != null) {
                this.settingsService.setVoiceGuidence(this.settingsService.voiceGuidenceJSONtoVoiceGuidenceSettings(resp.success.voiceguidance));
              }

              if (resp.success.voiceInputTutorialDone != undefined && resp.success.voiceInputTutorialDone != null) {
                this.settingsService.setVoiceInputTutorialDone(resp.success.voiceInputTutorialDone);
              }

              if (resp.success.unit != undefined && resp.success.unit != null) {
                this.settingsService.setUnit(this.settingsService.unitJSONtoUnitSettings(resp.success.unit));
              }

              if (resp.success.mapsettings != undefined && resp.success.mapsettings != null) {
                const mapsettings = resp.success.mapsettings;
                if (mapsettings.skin != undefined && mapsettings.skin != null) {
                  this.settingsService.setMap(this.settingsService.mapJSONtoMapSettings(resp.success.mapsettings));
                }
              }

              if (resp.success.tracking != undefined && resp.success.tracking != null) {
                this.settingsService.setTracking(resp.success.tracking);
              }

              if (resp.success.language != undefined && resp.success.language != null) {
                this.languageService.setLanguage(resp.success.language);
              }
            }

            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  setUserSettings(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      const unit = this.settingsService.getUnit();
      const routing = this.settingsService.getRouting();
      const voiceguidance = this.settingsService.getVoiceGuidence();
      const mapsettings = this.settingsService.getMap();
      const voiceInputTutorialDone = this.settingsService.getVoiceInputTutorialDone();
      const tracking = this.settingsService.getTracking();
      const language = this.languageService.getLanguage();

      this.httpService.updateUserSettings(unit, routing, voiceguidance, mapsettings, voiceInputTutorialDone, tracking, language)
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // set home/work address
  public setUserAddresses(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.updateUserAddress(this.settingsService.getAddresses())
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public setDisabledChargingStations(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.updateDisabledChargingStations(this.settingsService.getDisabledChargingStationIds())
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public setFavoriteChargingStations(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.updateFavoriteChargingStations(this.settingsService.getFavoriteChargingStationIds())
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public setDisabledChargingOperators(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.updateDisabledChargingOperators(this.settingsService.getDisabledChargingOperatorIds())
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public setFavoriteChargingOperators(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      const formData = new FormData();
      formData.append("ids", JSON.stringify(this.settingsService.getFavoriteChargingOperatorIds()));
      this.httpService.updateFavoriteChargingOperators(this.settingsService.getFavoriteChargingOperatorIds())
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          }, error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public voucherCheck(voucherId: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.voucherCheck(voucherId)
        .subscribe({
          next: (resp: any) => {
            if (!this.getIsAuthorized()) {
              this.ObservableVoucherInfoDialog.next(resp);
            }
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.ObservableVoucherInfoDialog.next(error.error);
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  public addVoucherSubscription(voucherId: string): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      localStorage.removeItem("voucherToken");
      if (!this.getUserSubscriptionType().includes(".proplus")) {
        this.httpService.addVoucherSubscription(voucherId)
          .subscribe({
            next: (resp: any) => {
              if (resp.success) {
                this.ObservableSubscriptionChangedDialog.next(SubscriptionType.ProplusMonthly);
                this.loadUserProfile().subscribe();
                observer.next(resp);
                observer.complete();
              }
              else {
                observer.next(resp);
                observer.complete();
              }
            },
            error: (error) => {
              this.ObservableVoucherInfoDialog.next(error.error);
              this.checkErrorStatus(error);
              observer.error(error);
              observer.complete();
            }
          });
      }
    });
  }

  /**
   * when the mobile app callback the frontend with success, we should check the backend that the subscription already activated
   */
  private handleSubscriptionSuccessCallback(subscriptionType: SubscriptionType) {
    this.fetchWithRetryForSubscription(2, 5000, subscriptionType, "true").subscribe({
      next: (resp) => {
        this.setUserData(resp);
        this.parseUserSubscription(resp);
        this.ObservableSubscriptionChangedDialog.next(subscriptionType);
      },
      error: (resp) => {
        this.ObservableSubscriptionType.next(this.ObservableSubscriptionType.value);
        this.ObservableSubscriptionFailed.next(true);
      }
    });
  }

  fetchWithRetryForSubscription(maxRetries: number, delayMs: number, subscriptionType: SubscriptionType, autorenew: string) {
    return this.httpService.getUser().pipe(
      switchMap((resp) => {

        if (resp?.subscriptions?.subscription_type &&
          ("evnavigation." + resp.subscriptions?.subscription_type + "." + resp.subscriptions?.renew_type == subscriptionType ||
            (this.webviewService.IsGPSTWebview() && device.ios()) && resp.subscriptions?.downgrade_at_period_end == 1)
          && resp.subscriptions?.autorenew == autorenew) {
          // Stop retrying if the desired object is found
          return of(resp);
        } else {
          // Trigger retry when the desired object is not found
          return throwError(() => new Error('Object not found'));
        }
      }),
      retry({
        count: maxRetries,
        delay: () => timer(delayMs)
      })
    );
  }

  private handleSubscriptionCancelCallback() {
    this.setUserSubscription(
      {
        AutoRenew: false, ExpireDate: null!, LastPaymentDate: null!, NextPaymentDate: null!,
        SubscriptionType: SubscriptionType.Free, Platform: SubscriptionPlatform.Web,
        DowngradeAtPeriodEnd: false
      });
  }

  public deleteUser(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.deleteUser()
        .subscribe({
          next: (resp: any) => {
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            this.checkErrorStatus(error);
            observer.error(error);
            observer.complete();
          }
        });
    });
  }

  // LOGOUT

  // log out 401 error status
  private checkErrorStatus(error): void {
    if (error.status && error.status == 401) {
      this.logout();
    }
  }

  public logout(): void {
    switch (this.provider) {
      case "google":
        this.signOutGoogle();
        break;
      case "facebook":
        this.signOutFacebook();
        break;
    }
    this.provider = "";
    this.settingsService.initSettings();
    this.setIsAuthorized(false);
    this.user = new User();
    this.user.Profile = new UserProfile();
    this.httpService.setToken(null!);
    this.social = false;
    this.setUserSubscription({ AutoRenew: false, ExpireDate: null!, LastPaymentDate: null!, NextPaymentDate: null!, SubscriptionType: SubscriptionType.Free, Platform: SubscriptionPlatform.Web, DowngradeAtPeriodEnd: false });
    this.cookieService.delete("evt");
    this.webviewService.ClearAccessToken();
  }

  public getHistory(carId: number): Observable<HistoryElement[]> {
    return new Observable((observer: Observer<HistoryElement[]>) => {
      // getting new history
      if (this.user.HistoryList[carId.toString()] == undefined) {
        this.httpService.getEvnaviActivities(carId.toString()).subscribe({
          next: (resp: any) => {
            resp = resp.map(el => {
              return {
                id: el.id,
                startLocation: el.from_loc,
                targetLocation: el.end_loc,
                startTime: el.from ? el.from.replace(/\s/, 'T') : el.from,
                distance: Math.round(el.distance * 10) / 10,
                totalTime: JSON.parse(el.time_duration),
                avgConsumption: (Math.round(el.averageConsumption * 10) / 10),
                totalConsumption: (Math.round(el.consumption * 10) / 10)
              }
            });

            this.user.HistoryList[carId.toString()] = resp;
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            observer.error(error);
            observer.complete();
          }
        });
      }
      // reload previously loaded history
      else {
        observer.next(this.user.HistoryList[carId.toString()]);
        observer.complete();
      }
    });
  }

  public getHistoryLastSelectedVehicle(): ECar | null {
    return this.historyLastSelectedVehicle;
  }

  public setHistoryLastSelectedVehicle(historyLastSelectedVehicle: ECar | null): void {
    this.historyLastSelectedVehicle = historyLastSelectedVehicle;
  }

  public getTrack(logId: number): Observable<TripPoint[]> {
    return new Observable((observer: Observer<TripPoint[]>) => {
      // getting new track
      if (this.user.TipList[logId.toString()] == undefined) {
        this.httpService.readEvnaviFiles(logId.toString()).subscribe({
          next: (resp: any) => {
            //resp = JSON.parse(resp);
            this.user.TipList[logId] = resp;
            observer.next(resp);
            observer.complete();
          },
          error: (error) => {
            observer.error(error);
            observer.complete();
          }
        });
      }
      // reload previously loaded track
      else {
        observer.next(this.user.TipList[logId.toString()]);
        observer.complete();
      }
    });
  }

  public uploadLog(userVehicleId, data): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.logEvnaviData(userVehicleId, data).subscribe({
        next: (resp: any) => {
          observer.next(resp);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
          observer.complete();
        }
      });
    });
  }

  public deleteLogById(carId: number, logId: number): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.deleteEvnaviLogById(logId.toString()).subscribe({
        next: (resp: any) => {
          this.user.HistoryList[carId.toString()] = undefined;
          observer.next(resp);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
          observer.complete();
        }
      });
    });
  }

  public deleteLogByVehicle(carId: number): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.httpService.deleteEvnaviLogByVehicle(carId.toString()).subscribe({
        next: (resp: any) => {
          this.user.HistoryList[carId.toString()] = undefined;
          observer.next(resp);
          observer.complete();
        }, error: (error) => {
          observer.error(error);
          observer.complete();
        }
      });
    });
  }

  // SOCIAL SIGN IN

  private handleFacebookSignInResponse(isSucces: boolean, userId: string, accessToken: string): void {
    if (isSucces) {
      //response.authResponse.accessToken
      this.authenticateSocial("facebook", userId, accessToken).subscribe((resp) => {
        this.ObservableSocialLoginResponse.next({ AuthResponse: resp.success, ErrorMessage: resp.failed });
      });
    } else {
    }
  }

  public handleGoogleSignInResponse(isSucces: boolean, userId: string, accessToken: string): void {
    if (isSucces) {
      this.authenticateSocial("google", userId, accessToken).subscribe((resp) => {
        this.ObservableSocialLoginResponse.next({ AuthResponse: resp.success, ErrorMessage: resp.failed });
      });
    }
  }

  private handleAppleSignInResponse(isSuccess: boolean, userId: string, accessToken: string): void {
    if (isSuccess) {
      this.authenticateSocial("apple", userId, accessToken, true).subscribe((resp) => {
        this.ObservableSocialLoginResponse.next({ AuthResponse: resp.success, ErrorMessage: resp.failed });
      });
    }
  }

  /* sign in wih facebook */
  public signInWithFacebook(): void {
    if (this.webviewService.IsGPSTWebview()) {
      this.webviewService.AddFacebookSignInCallback((isSucces: boolean, userId: string, accessToken: string) => { this.handleFacebookSignInResponse(isSucces, userId, accessToken); });
      this.webviewService.FacebookSignIn();
    }
    else {
      window['FB'].login((response) => {
        if (response.authResponse) {
          this.handleFacebookSignInResponse(response.authResponse, response.authResponse.userID, response.authResponse.accessToken);
        }
      }, { scope: 'email' });
    }
  }

  private signOutFacebook(): void {
    if (this.webviewService.IsGPSTWebview()) {
      this.webviewService.FacebookLogout();
    }
    else {
      if (window['FB']) {
        window['FB'].getLoginStatus(function (response) {
          if (response && response.status === 'connected') {
            window['FB'].logout(function (response) {
            });
          }
        });
      }
    }
  }

  /* apple login */
  public signInWithApple(): void {
    if (this.webviewService.IsGPSTWebview() && device.ios()) {
      this.webviewService.AppleSignIn();
    }
    else {
      window["AppleID"].auth.init({
        clientId: environment.APPLE_CLIENT_ID,
        redirectURI: environment.APPLE_REDIRECT_URI,
        scope: "name email"
      });

      window["AppleID"].auth.signIn();
    }
  }

  public signInWithGoogle(): void {
    if (this.webviewService.IsGPSTWebview()) {
      this.webviewService.AddGoogleSignInCallback((isSucces: boolean, userId: string, accessToken: string) => { this.handleGoogleSignInResponse(isSucces, userId, accessToken); });
      this.webviewService.GoogleSignIn();
    }
    else {
      this.clickGisButton();
    }
  }

  /**
   * Clicks the hidden GIS button rendered in initGoogleLogin(); the credential arrives
   * in the callback registered there. Called from a user click, so the popup keeps the
   * user activation while we briefly wait for Google's markup if it is still loading.
   */
  private clickGisButton(attempt: number = 0): void {
    const gisButton = this.gisButtonHost?.querySelector<HTMLElement>('[role="button"]');
    if (gisButton) {
      gisButton.click();
      return;
    }

    if (attempt < AccountService.GIS_POLL_MAX) {
      setTimeout(() => this.clickGisButton(attempt + 1), AccountService.GIS_POLL_MS);
      return;
    }

    console.warn('Google Sign-In: the GIS button was not rendered (gsi/client script missing or blocked?)');
  }

  private signOutGoogle(): void {
    if (this.webviewService.IsGPSTWebview()) {
      this.webviewService.GoogleLogOut();
    }
  }
}
